import type { ChatTransport, UIMessage } from 'ai';
import type { ChatService, ChatServiceConfig } from './types';
import { MockChatService } from './mock/MockChatService';
import { MockChatTransport } from './transport/MockChatTransport';
import { RealChatService } from './real/RealChatService';
import { RealChatTransport } from './transport/RealChatTransport';

// Re-export types
export type { ChatService, ChatServiceConfig, RealChatServiceConfig } from './types';
export type { MockChatServiceConfig } from './mock/MockChatService';
export type { MockChatTransportConfig } from './transport/MockChatTransport';
export type { RealChatTransportConfig } from './transport/RealChatTransport';

// Re-export implementations
export { MockChatService } from './mock/MockChatService';
export { MockChatTransport } from './transport/MockChatTransport';
export { RealChatService } from './real/RealChatService';
export { RealChatTransport } from './transport/RealChatTransport';

/**
 * Default API base URL for the backend
 */
const DEFAULT_API_BASE_URL = 'http://localhost:8000';

/**
 * Default configuration - uses mock service
 */
const DEFAULT_CONFIG: ChatServiceConfig = {
  useMock: true,
  mockStreamDelayMs: 25,
  apiBaseUrl: DEFAULT_API_BASE_URL,
};

/**
 * Get the current chat service configuration
 */
export function getChatServiceConfig(): ChatServiceConfig {
  // In Vite, use import.meta.env; in Node/Next, use process.env
  const useMock = getEnvVar('VITE_USE_MOCK_CHAT', 'true') === 'true';
  const mockStreamDelayMs = parseInt(getEnvVar('VITE_MOCK_STREAM_DELAY_MS', '25'), 10);
  const apiBaseUrl = getEnvVar('VITE_API_BASE_URL', DEFAULT_API_BASE_URL) || DEFAULT_API_BASE_URL;
  const debug = getEnvVar('VITE_AI_DEBUG_ON', 'false') === 'true';

  return {
    useMock,
    mockStreamDelayMs,
    apiBaseUrl,
    debug,
  };
}

/**
 * Create a chat service based on configuration
 */
export function createChatService(
  config: Partial<ChatServiceConfig> = {}
): ChatService {
  const finalConfig = { ...DEFAULT_CONFIG, ...getChatServiceConfig(), ...config };

  if (finalConfig.useMock) {
    return new MockChatService({
      streamDelayMs: finalConfig.mockStreamDelayMs ?? 25,
      debug: finalConfig.debug ?? false,
    });
  }

  return new RealChatService({
    apiBaseUrl: finalConfig.apiBaseUrl ?? DEFAULT_API_BASE_URL,
    debug: finalConfig.debug ?? false,
  });
}

/**
 * Create a chat transport for use with AI SDK's Chat class
 */
export function createChatTransport(options: {
  model?: string;
  body?: Record<string, unknown>;
} = {}): ChatTransport<UIMessage> {
  const config = getChatServiceConfig();

  if (config.useMock) {
    return new MockChatTransport({
      model: options.model ?? (options.body?.model as string),
      streamDelayMs: config.mockStreamDelayMs,
      debug: config.debug,
    });
  }

  return new RealChatTransport({
    apiBaseUrl: config.apiBaseUrl ?? DEFAULT_API_BASE_URL,
    debug: config.debug ?? false,
  });
}

/**
 * Get the chat service for title generation and other non-streaming operations
 */
let chatServiceInstance: ChatService | null = null;

export function getChatService(): ChatService {
  if (!chatServiceInstance) {
    chatServiceInstance = createChatService();
  }
  return chatServiceInstance;
}

/**
 * Helper to get environment variables with fallback
 * Works in both Vite (import.meta.env) and Node (process.env) environments
 */
function getEnvVar(key: string, defaultValue: string): string {
  // Check Vite environment first
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const value = import.meta.env[key];
    if (value !== undefined) {
      return value;
    }
  }

  // Check process.env (Node/Next.js)
  if (typeof process !== 'undefined' && process.env) {
    const value = process.env[key];
    if (value !== undefined) {
      return value;
    }
  }

  return defaultValue;
}
